import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { signInRoute } from "src/routing/domains/authPaths";
import { personalDashboardRoute } from "src/routing/domains/personalDashboardPaths";

/**
 * Save a deep link to redirect to after sign in.
 *
 * If the user isn't authenticated, we check for a path to set as the deepLink.
 * We skip saving the deepLink if the user navigates to sign-in.
 * Additionally, since we redirect to the personal dashboard when a user logs in or navigates to "/",
 * these paths don't need to be saved as a deepLink.
 *
 * Writes are skipped while auth is still resolving so we don't treat a logged-in user's initial
 * page load as an unauthenticated visit (AppShell starts with isAuthenticated=false until currentUser() returns).
 *
 * Do not clear the saved deepLink here. After OAuth, GuestShell sends the user to `/`,
 * and that index route reads and clears `deepLink` via `RootIndexRedirect`.
 */
export function useDeepLink(isAuthenticated: boolean, isAuthenticating: boolean) {
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticating) return;

    const deepLink = localStorage.getItem("deepLink");
    const skipDeepLink = [signInRoute.path, "/", personalDashboardRoute.path].includes(location.pathname);
    if (!isAuthenticated && !skipDeepLink && !deepLink) {
      const currentPath = location.pathname + location.search;
      localStorage.setItem("deepLink", currentPath);
    }
  }, [isAuthenticated, isAuthenticating, location]);
}
